// #623 — the cached-coverage status badge shown on the Shipping tab.
//
// GET /shipping/coverage (and its embedded twin) returns, next to the live
// Shopify read, the state of the CACHED coverage the shopper-facing filters and
// the checkout gate run on:
//   coverage_status      'VALID' | 'STALE' | 'UNKNOWN' | 'ERROR'
//   coverage_updated_at  ISO timestamp of the last SUCCESSFUL refresh, or null
//
// This module turns that pair into what the badge renders (variant + i18n key +
// interpolation vars). Pure, no React, no map deps — so it is unit-testable
// under CRA's Jest without importing ShippingTab.js (react-simple-maps is ESM).

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * @param {Object} coverage  the /shipping/coverage response (any shape)
 * @param {Date|number} [now] test seam
 * @returns {{
 *   variant: 'ok'|'warn'|'error',
 *   key: string,            // i18n key under 'shipping.status*'
 *   vars: Object,           // {time} for VALID, {days} for STALE
 *   updatedAt: Date|null
 * }}
 */
export function describeCoverageStatus(coverage, now = Date.now()) {
  const raw = typeof coverage?.coverage_status === 'string'
    ? coverage.coverage_status.trim().toUpperCase()
    : '';
  const updatedAt = parseDate(coverage?.coverage_updated_at);
  const nowMs = now instanceof Date ? now.getTime() : Number(now);

  if (raw === 'VALID') {
    return {
      variant: 'ok',
      key: 'shipping.statusValid',
      vars: { time: formatDateTime(updatedAt) },
      updatedAt,
    };
  }

  if (raw === 'STALE') {
    const ageMs = updatedAt ? Math.max(0, nowMs - updatedAt.getTime()) : null;
    const days = ageMs == null ? null : Math.floor(ageMs / DAY_MS);
    return {
      variant: 'warn',
      // Without a usable timestamp we cannot say "n days" — use the dateless copy.
      key: days == null ? 'shipping.statusStaleNoDate' : 'shipping.statusStale',
      vars: { days: days == null ? '' : String(days) },
      updatedAt,
    };
  }

  // UNKNOWN / ERROR / missing / unrecognised → the same "could not read" hint.
  return {
    variant: 'error',
    key: 'shipping.statusUnknown',
    vars: {},
    updatedAt,
  };
}

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Short, locale-aware "21 Sep 2026, 14:05" — falls back to the ISO string when
// Intl is unavailable (very old engines) so the badge never throws.
export function formatDateTime(date) {
  if (!date) return '—';
  try {
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return date.toISOString();
  }
}
